/**
 * Mailshot Repository
 * 
 * Extends BaseRepository with mailshot-specific operations.
 * Common CRUD inherited from BaseRepository - no duplication!
 */

import { httpsCallable } from 'firebase/functions';
import { Mailshot } from './Mailshot';
import { BaseRepository } from './BaseRepository';

export class MailshotRepository extends BaseRepository {
  constructor(db, functions) {
    super(db, functions, 'discovery_mailshots', Mailshot);
  }

  // ========================================
  // SPECIALIZED FINDER METHODS
  // ========================================

  /**
   * Find mailshots by campaign ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Mailshot[]>} - Array of Mailshot instances
   */
  async findByCampaignId(campaignId) {
    return this.findAll({
      where: [['campaignId', '==', campaignId]]
    });
  }

  // ========================================
  // OVERRIDDEN METHODS
  // ========================================

  /**
   * Create a new mailshot (override to add validation)
   * @param {Mailshot} mailshot - Mailshot instance
   * @returns {Promise<string>} - Created mailshot ID
   */
  async create(mailshot) {
    // Validate before creating
    mailshot.validate();
    
    const docData = mailshot.toFirestore();
    const created = await super.create(docData); // Auto-generated ID
    
    return created.id;
  }

  /**
   * Delete a mailshot (override to delegate to Cloud Function for cascade)
   * @param {string} id - Mailshot ID
   * @returns {Promise<void>}
   */
  /**
   * The Mailshots report (Campaigns beta): each sent mailshot and the quotes after it, in windows from its own
   * send date (functions/mailshots/mailshotReport.js). Read only; nothing is stored.
   */
  async getReport() {
    const fn = httpsCallable(this.functions, 'getMailshotReport');
    const res = await fn({});
    return res.data;
  }

  async delete(id) {
    // Delegate to Cloud Function for cascade delete
    const deleteMailshotFn = httpsCallable(this.functions, 'deleteMailshot');
    await deleteMailshotFn({ mailshotId: id });
  }

  // ========================================
  // CLOUD FUNCTION DELEGATIONS
  // ========================================

  /**
   * Recalculate cached counts for a mailshot (delegates to Cloud Function)
   * @param {string} id - Mailshot ID
   * @returns {Promise<void>}
   */
  async recalculateCounts(id) {
    const recalculateCountsFn = httpsCallable(this.functions, 'recalculateMailshotCounts');
    await recalculateCountsFn({ mailshotId: id });
  }

  /**
   * Mark mailshot as mailed (delegates to Cloud Function)
   * @param {string} id - Mailshot ID
   * @param {Date} mailedDate - Date mailed
   * @returns {Promise<Object>} the function's answer: how many homes were stamped, how many on the list are
   *   marked do not contact and were NOT stamped, and how many have no record
   */
  async markAsMailed(id, mailedDate) {
    const markAsMailedFn = httpsCallable(this.functions, 'markMailshotAsMailed');
    const result = await markAsMailedFn({
      mailshotId: id, 
      mailedDate: mailedDate?.toISOString() 
    });
    return result && result.data;
  }

  /**
   * Set or clear the go link printed on a mailshot (delegates to Cloud Function)
   * @param {string} id - Mailshot ID
   * @param {string|null} link - The link as printed, or null to clear it
   * @returns {Promise<{link: string|null, redirects: number|null}>}
   */
  async setPrintedGoLink(id, link) {
    // The go link printed on the mailshot (its QR code or the link as text). Checked and written by the function:
    // it refuses a campaign go does not know and a link another mailshot has. Answers { link, redirects }.
    const fn = httpsCallable(this.functions, 'setMailshotGoLink');
    const result = await fn({ mailshotId: id, link: link || null });
    return result && result.data;
  }

  /**
   * Unmark mailshot as mailed (delegates to Cloud Function)
   * @param {string} id - Mailshot ID
   * @returns {Promise<void>}
   */
  async unmarkAsMailed(id) {
    const unmarkAsMailedFn = httpsCallable(this.functions, 'unmarkMailshotAsMailed');
    await unmarkAsMailedFn({ mailshotId: id });
  }

  /**
   * Update delivery status for properties in mailshot (delegates to Cloud Function)
   * @param {string[]} propertyIds - Property IDs
   * @param {string} deliveryStatus - New delivery status
   * @returns {Promise<void>}
   */
  async updateDeliveryStatus(propertyIds, deliveryStatus) {
    const updateStatusFn = httpsCallable(this.functions, 'updatePropertiesDeliveryStatus');
    await updateStatusFn({ propertyIds, deliveryStatus });
  }

  /**
   * Add properties to mailshot (delegates to Cloud Function)
   * @param {string} mailshotId - Mailshot ID
   * @param {string[]} propertyIds - Property IDs to add
   * @returns {Promise<void>}
   */
  async addProperties(mailshotId, propertyIds) {
    const addPropertiesFn = httpsCallable(this.functions, 'addPropertiesToMailshot');
    await addPropertiesFn({ mailshotId, propertyIds });
  }

  /**
   * Remove properties from mailshot (delegates to Cloud Function)
   * @param {string} mailshotId - Mailshot ID
   * @param {string[]} propertyIds - Property IDs to remove
   * @returns {Promise<void>}
   */
  async removeProperties(mailshotId, propertyIds) {
    const removePropertiesFn = httpsCallable(this.functions, 'removePropertiesFromMailshot');
    await removePropertiesFn({ mailshotId, propertyIds });
  }

  /**
   * Calculate promoters in mailshot area (delegates to Cloud Function)
   *
   * Finds all promoters within the mailshot's search circles (plus buffer),
   * calculates nearby property counts and nearest property distances,
   * and stores results on the mailshot document.
   *
   * @param {string} mailshotId - Mailshot ID
   * @param {number} catchmentBuffer - Buffer distance in miles beyond search radius (default 1)
   * @param {number} nearbyRadius - Radius for nearby properties count (default 1)
   * @param {number} matchingThreshold - Minimum similarity score for matching (default 0.70)
   * @returns {Promise<{success: boolean, promotersCount: number, warnings: string[], searchCentersCount: number}>}
   */
  async calculatePromoters(mailshotId, catchmentBuffer = 1, nearbyRadius = 1, matchingThreshold = 0.70) {
    const calculatePromotersFn = httpsCallable(this.functions, 'calculateMailshotPromoters');
    const result = await calculatePromotersFn({ mailshotId, catchmentBuffer, nearbyRadius, matchingThreshold });
    return result.data;
  }
}
